import SearchAPI from '../../api/search';
import types from '../mutation-types';
export const initialState = {
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    // True when at least one search timed out or errored, so an empty result
    // set must not be presented as "nothing matched".
    isDegraded: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

// Shared runner for the three search flavours. An empty result set is only
// trustworthy when the backend actually finished: `meta.timed_out` (statement
// timeout on the server) and a request failure both mean "we don't know", so we
// raise `isDegraded` and let the view say so instead of rendering "no results".
// `request` is a thunk rather than a bare method reference: SearchAPI methods
// read `this.url`, so passing them unbound would throw.
const runSearch = async (commit, { request, key, setType, flagType }) => {
  commit(setType, []);
  commit(flagType, { isFetching: true });
  try {
    const { data } = await request();
    commit(setType, data.payload[key]);
    if (data.meta?.timed_out) {
      commit(types.FULL_SEARCH_SET_UI_FLAG, { isDegraded: true });
    }
  } catch (error) {
    commit(types.FULL_SEARCH_SET_UI_FLAG, { isDegraded: true });
  } finally {
    commit(flagType, { isFetching: false });
  }
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  async fullSearch({ commit, dispatch }, { q }) {
    if (!q) {
      return;
    }
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
      isDegraded: false,
    });
    try {
      await Promise.all([
        dispatch('contactSearch', { q }),
        dispatch('conversationSearch', { q }),
        dispatch('messageSearch', { q }),
      ]);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.FULL_SEARCH_SET_UI_FLAG, {
        isFetching: false,
        isSearchCompleted: true,
      });
    }
  },
  async contactSearch({ commit }, { q }) {
    await runSearch(commit, {
      request: () => SearchAPI.contacts({ q }),
      key: 'contacts',
      setType: types.CONTACT_SEARCH_SET,
      flagType: types.CONTACT_SEARCH_SET_UI_FLAG,
    });
  },
  async conversationSearch({ commit }, { q }) {
    await runSearch(commit, {
      request: () => SearchAPI.conversations({ q }),
      key: 'conversations',
      setType: types.CONVERSATION_SEARCH_SET,
      flagType: types.CONVERSATION_SEARCH_SET_UI_FLAG,
    });
  },
  async messageSearch({ commit }, { q }) {
    await runSearch(commit, {
      request: () => SearchAPI.messages({ q }),
      key: 'messages',
      setType: types.MESSAGE_SEARCH_SET,
      flagType: types.MESSAGE_SEARCH_SET_UI_FLAG,
    });
  },
  async clearSearchResults({ commit }) {
    commit(types.MESSAGE_SEARCH_SET, []);
    commit(types.CONVERSATION_SEARCH_SET, []);
    commit(types.CONTACT_SEARCH_SET, []);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, records) {
    state.contactRecords = records;
  },
  [types.CONVERSATION_SEARCH_SET](state, records) {
    state.conversationRecords = records;
  },
  [types.MESSAGE_SEARCH_SET](state, records) {
    state.messageRecords = records;
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
